'use client';

import { useEffect } from 'react';

import { animate } from 'motion/react';

type Playback = { stop: () => void };

let playback: Playback | null = null;
let detach: (() => void) | null = null;

/** Hash id if this href points at an element on the current page. */
function samePageHashId(href: string): string | null {
  if (href.startsWith('#') && href.length > 1) return href.slice(1);
  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) return null;
    if (url.pathname !== window.location.pathname) return null;
    if (url.hash.length <= 1) return null;
    return url.hash.slice(1);
  } catch {
    return null;
  }
}

function scrollToTarget(target: HTMLElement) {
  detach?.();

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const margin = Number.parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
  const to = target.getBoundingClientRect().top + window.scrollY - margin;

  if (reducedMotion) {
    window.scrollTo({ top: to });
    return;
  }

  const from = window.scrollY;
  const distance = Math.abs(to - from);
  if (distance < 2) return;

  function release() {
    window.removeEventListener('wheel', interrupt);
    window.removeEventListener('touchstart', interrupt);
    playback = null;
    detach = null;
  }

  function interrupt() {
    playback?.stop();
    release();
  }

  detach = interrupt;

  // Ease-out expo, duration scaled to distance so a short hop is snappy and
  // the long drop from the hero to How it works still lands, not snaps.
  playback = animate(from, to, {
    duration: Math.min(1.15, Math.max(0.55, distance / 1500)),
    ease: [0.16, 1, 0.3, 1],
    onUpdate(value) {
      window.scrollTo(0, value);
    },
    onComplete: release,
  });

  window.addEventListener('wheel', interrupt, { passive: true });
  window.addEventListener('touchstart', interrupt, { passive: true });
}

export function SmoothHashScroll() {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      const eventTarget = e.target;
      if (!(eventTarget instanceof Element)) return;
      const anchor = eventTarget.closest('a');
      if (!anchor || anchor.hasAttribute('download')) return;
      if (anchor.target && anchor.target !== '_self') return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      const id = samePageHashId(href);
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      scrollToTarget(target);
      history.pushState(null, '', `#${id}`);
      // Move keyboard focus into the target so Tab continues from there.
      // tabindex=-1 lets non-focusable elements (like <section>) receive focus
      // programmatically without entering the tab order.
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);
  return null;
}
